import { Fancybox, Swiper, A11y, Navigation, Pagination, Thumbs } from 'global-modules';

class ProductMediaSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this?.dataset?.options) {
      this.options = JSON.parse(this.dataset.options);
    }
    this.options.modules = [A11y];

    if (this.options.pagination) {
      this.options.modules.push(Pagination);
    }

    if (this.options.navigation) {
      this.options.modules.push(Navigation);
    }

    this.swiperMain = this.querySelector('[data-product-media-swiper]');
    this.fancybox();

    // Swiper only runs on mobile; on desktop the slides are a vertical
    // column in page flow (see the stylesheet in product-media-swiper.liquid)
    this.desktopMedia = window.matchMedia('(min-width: 768px)');
    this.toggleSwipersHandler = () => this.toggleSwipers();
    this.toggleSwipers();
    this.desktopMedia.addEventListener('change', this.toggleSwipersHandler);

    // Slide to variant from URL on initial load
    const urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'));
    if (urlVariantId) this.slideToVariant({ detail: { variantId: urlVariantId } });

    // Reduce actions (So the event can also be removed)
    this.slideToVariantHandler = (event) => {
      event.promise
        ?.then(({ variant }) => {
          if (variant) this.slideToVariant({ detail: { variantId: variant.id } });
        })
        .catch(() => {});
    };

    // Listen for variant change and go to slide
    document.addEventListener('shopify:product:select', this.slideToVariantHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.slideToVariantHandler);
    this.desktopMedia.removeEventListener('change', this.toggleSwipersHandler);
  }

  toggleSwipers() {
    if (this.desktopMedia.matches) {
      this.swiperMainInstance?.destroy(true, true);
      this.swiperThumbsInstance?.destroy(true, true);
      this.swiperMainInstance = null;
      this.swiperThumbsInstance = null;
    }
    else if (!this.swiperMainInstance) {
      this.thumbnailSwiper();
      this.mainSwiper();
    }
  }

  thumbnailSwiper() {
    if (!Swiper) return false;

    this.swiperThumbs = this.querySelector('[data-product-media-thumbnails-slider]');
    this.swiperThumbsOptions = {
      a11y: true,
      observer: true,
      spaceBetween: 8,
      slidesPerView: 'auto',
      modules: [A11y, Thumbs],
    };

    this.swiperThumbsInstance = new Swiper(this.swiperThumbs, this.swiperThumbsOptions);

    if (Shopify.designMode && this.swiperThumbsInstance) {
      window.addEventListener('shopify:section:load', () => {
        setTimeout(() => {
          this.swiperThumbsInstance?.update();
        }, 300);
      });
    }
  }

  mainSwiper() {
    if (!Swiper) return false;

    let thumbOptions;
    if (this.swiperThumbsInstance) {
      if (!this.options.modules.includes(Thumbs)) this.options.modules.push(Thumbs);
      thumbOptions = {
        thumbs: {
          swiper: this.swiperThumbsInstance.hostEl,
          slideThumbActiveClass: 'swiper-slide-thumb-active',
          multipleActiveThumbs: false,
          autoScrollOffset: 1,
        },
      };
    }

    let initialSlideIndex = 0;
    const initialVariantId = parseInt(this.swiperMain.dataset.productMediaSlider);

    if (initialVariantId) {
      const initialSlide = this.matchVariantIdWithSlide(initialVariantId);
      initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
    }

    this.swiperMainOptions = {
      a11y: true,
      rewind: true,
      observer: true,
      initialSlide: initialSlideIndex,
      modules: this.options.modules,
      ...this.options,
      ...thumbOptions,
    };

    this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

    if (Shopify.designMode && this.swiperMainInstance) {
      window.addEventListener('shopify:section:load', () => {
        this.swiperMainInstance?.update();
      });
    }
  }

  fancybox() {
    if (!Fancybox) return false;

    Fancybox.bind('[data-fancybox="product-media-swiper"]', {
      dragToClose: false,
      showClass: 'f-fadeIn',
      hideClass: 'f-fadeOut',
      mainClass: 'fancybox-products',
      Carousel: {
        transition: 'slide',
      },
      // Disable image zoom animation on opening and closing
      Images: {
        zoom: false,
      },
      Thumbs: {
        type: 'classic',
      },
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    });
  }

  slideToVariant(event) {
    if (event?.detail?.variantId) {
      const slideIndex = this.matchVariantIdWithSlide(+event.detail.variantId);
      if (typeof slideIndex == 'number') this.goToSlide(slideIndex);
    }
  }

  matchVariantIdWithSlide(id) {
    if (!id || !this.swiperMain) return false;

    const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

    const slide = [...slides].find((slide) => {
      const slideVariants = JSON.parse(slide.dataset.productVariantImage);
      if (slideVariants.includes(id)) return slide;
    });

    if (!slide) return false;

    return this.getNodeIndex(slide);
  }

  goToSlide(slide) {
    if (typeof slide != 'number' && typeof slide != 'object') return false;

    const index = typeof slide == 'number' ? parseInt(slide) : this.getNodeIndex(slide);

    if (this.swiperMainInstance) {
      this.swiperMainInstance.slideTo(index);
    }
    else if (this.swiperMain) {
      // Desktop: slides are in page flow — scroll the page to the slide
      // (scroll-margin on the slide keeps it clear of the sticky header)
      const target = this.swiperMain.querySelectorAll('.swiper-slide')[index];
      if (target) target.scrollIntoView({ behavior: 'smooth',
        block: 'start' });
    }
  }

  getNodeIndex(node) {
    return Array.from(node.parentNode.children).indexOf(node);
  }
}

if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', ProductMediaSlider);
}
